import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  Chip,
  IconButton,
  Button,
  Divider,
  Tooltip,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  Send as SendIcon,
  AttachFile as AttachIcon,
  Phone as PhoneIcon,
  Voicemail as VoicemailIcon,
  Fax as FaxIcon,
  Sms as SmsIcon,
  MoreVert as MoreIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Archive as ArchiveIcon,
  Download as DownloadIcon,
  PlayArrow as PlayIcon,
  PictureAsPdf as PdfIcon,
  NoteAlt as NoteIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  AssignmentInd as AssignIcon,
  Label as LabelIcon,
  ArrowForward as ArrowIcon,
  Circle as CircleIcon,
  Flag as FlagIcon,
  Schedule as ScheduleIcon,
  Mic as MicIcon,
  EmojiEmotions as EmojiIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow, isToday, isYesterday, parseISO } from 'date-fns';

interface SpruceMessage {
  id: string;
  text?: string;
  author: {
    displayName: string;
    type: 'team' | 'patient';
    initials?: string;
  };
  createdAt: string;
  direction: 'inbound' | 'outbound';
  isInternalNote?: boolean;
  isRemoved?: boolean;
  removedBy?: string;
  attachments?: {
    id: string;
    type: 'image' | 'pdf' | 'fax' | 'voicemail' | 'audio';
    url?: string;
    name?: string;
    transcription?: string;
    duration?: number;
  }[];
}

interface SpruceConversation {
  id: string;
  contactName: string;
  contactPhone: string;
  contactAvatar?: string;
  lastMessage?: string;
  lastMessageAt: string;
  unreadCount: number;
  isStarred: boolean;
  isArchived?: boolean;
  type: 'sms' | 'phone' | 'voicemail' | 'fax';
  assignedTo?: string;
  messages: SpruceMessage[];
  participants?: { name: string; phone: string; isTeam?: boolean }[];
  subject?: string;
  tags?: string[];
}

// Mock data based on screenshots
const mockConversations: SpruceConversation[] = [
  {
    id: '1',
    contactName: 'Ronnie Relay',
    contactPhone: '(305) 699-4969',
    lastMessage: 'SMM Q',
    lastMessageAt: new Date().toISOString(),
    unreadCount: 0,
    isStarred: false,
    type: 'sms',
    assignedTo: 'Ronnie Relay',
    participants: [
      { name: 'Ronnie Relay', phone: '(305) 699-4969', isTeam: false },
      { name: 'Victor Levitan, MD, PLLC', phone: '(847) 865-9088', isTeam: true }
    ],
    tags: ['import_Dec222025_1630'],
    messages: [
      {
        id: 'm1',
        text: 'AI Triage Creation Testing Staff to PT.',
        author: { displayName: 'Ronnie Samuel', type: 'team', initials: 'RS' },
        createdAt: '2025-10-21T08:17:00Z',
        direction: 'outbound',
      },
      {
        id: 'm2',
        text: 'Simulating PT to Clinic via Spruce Msg...confirmation OK.',
        author: { displayName: 'Ronnie Relay', type: 'patient' },
        createdAt: '2025-10-21T08:19:00Z',
        direction: 'inbound',
      },
      {
        id: 'm3',
        text: 'Thank you for confirming the message from PT to the clinic.',
        author: { displayName: 'Victor Levitan, MD, PLLC', type: 'team' },
        createdAt: '2025-10-21T08:20:00Z',
        direction: 'outbound',
      },
      {
        id: 'm4',
        text: 'Appointment creation check from task details.',
        author: { displayName: 'Ronnie Relay', type: 'patient' },
        createdAt: '2025-12-04T14:43:00Z',
        direction: 'inbound',
      },
      {
        id: 'm5',
        text: '(Removed)',
        author: { displayName: 'Victor Levitan, MD, PLLC', type: 'team' },
        createdAt: '2025-12-23T12:34:00Z',
        direction: 'outbound',
        isRemoved: true,
        removedBy: 'Ronnie Samuel',
      },
    ],
  },
  {
    id: '2',
    contactName: '(360) 200-5100',
    contactPhone: '(360) 200-5100',
    lastMessage: '(360) 200-5100:',
    lastMessageAt: new Date(Date.now() - 3600000).toISOString(),
    unreadCount: 1,
    isStarred: false,
    type: 'sms',
    messages: [],
  },
  {
    id: '3',
    contactName: '(312) 243-8678',
    contactPhone: '(312) 243-8678',
    lastMessage: 'Fax delivery confirmed',
    lastMessageAt: new Date(Date.now() - 7200000).toISOString(),
    unreadCount: 0,
    isStarred: false,
    type: 'fax',
    messages: [
      {
        id: 'm6',
        text: 'Fax (with attachment IGoldmanTeamstersForm.pdf) successfully sent to (312) 243-8678.',
        author: { displayName: 'Victor Levitan', type: 'team', initials: 'VL' },
        createdAt: '2025-12-23T17:06:00Z',
        direction: 'outbound',
        attachments: [{
          id: 'a1',
          type: 'fax',
          name: 'IGoldmanTeamstersForm.pdf',
          url: '#',
        }],
      },
    ],
  },
  {
    id: '4',
    contactName: 'Richard Friedman',
    contactPhone: '(847) 555-1234',
    lastMessage: 'Called Victor Levitan, MD, unanswered',
    lastMessageAt: new Date(Date.now() - 86400000).toISOString(),
    unreadCount: 0,
    isStarred: false,
    type: 'phone',
    assignedTo: 'Victor Levitan, MD',
    messages: [],
  },
];

const SpruceInbox: React.FC = () => {
  const [conversations, setConversations] = useState<SpruceConversation[]>(mockConversations);
  const [selectedConversation, setSelectedConversation] = useState<SpruceConversation | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [messageType, setMessageType] = useState<'sms' | 'note'>('sms');
  const [sidebarSection, setSidebarSection] = useState<string>('all');
  const [showDetails, setShowDetails] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(['inbox', 'contacts']);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (selectedConversation) {
      scrollToBottom();
    }
  }, [selectedConversation?.messages.length]);

  const filteredConversations = conversations.filter(conv => {
    if (sidebarSection === 'starred' && !conv.isStarred) return false;
    if (sidebarSection === 'archived' && !conv.isArchived) return false;
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      return (
        conv.contactName.toLowerCase().includes(query) ||
        conv.contactPhone.includes(query) ||
        conv.lastMessage?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const newMsg: SpruceMessage = {
      id: `m${Date.now()}`,
      text: newMessage,
      author: { displayName: 'You', type: 'team' },
      createdAt: new Date().toISOString(),
      direction: 'outbound',
      isInternalNote: messageType === 'note',
    };

    setConversations(prev =>
      prev.map(conv =>
        conv.id === selectedConversation.id
          ? { ...conv, messages: [...conv.messages, newMsg], lastMessage: newMessage, lastMessageAt: newMsg.createdAt }
          : conv
      )
    );
    setSelectedConversation(prev =>
      prev ? { ...prev, messages: [...prev.messages, newMsg] } : prev
    );
    setNewMessage('');
  };

  const handleToggleStar = (convId: string) => {
    setConversations(prev =>
      prev.map(conv =>
        conv.id === convId ? { ...conv, isStarred: !conv.isStarred } : conv
      )
    );
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const getTypeIcon = (type: string, hasUnread: boolean) => {
    const color = hasUnread ? 'primary' : 'action';
    switch (type) {
      case 'sms': return <SmsIcon fontSize="small" color={color as any} />;
      case 'phone': return <PhoneIcon fontSize="small" color={color as any} />;
      case 'voicemail': return <VoicemailIcon fontSize="small" color={color as any} />;
      case 'fax': return <FaxIcon fontSize="small" color={color as any} />;
      default: return <SmsIcon fontSize="small" color={color as any} />;
    }
  };

  const formatMessageDate = (dateStr: string) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return format(date, 'h:mm a');
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MM/dd/yyyy');
  };

  const groupMessagesByDate = (messages: SpruceMessage[]) => {
    const groups: { [key: string]: SpruceMessage[] } = {};
    messages.forEach(msg => {
      const date = format(parseISO(msg.createdAt), 'EEEE, MMMM d');
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });
    return groups;
  };

  // Sidebar sections
  const sidebarItems = [
    { id: 'inbox', label: 'Inbox', icon: null, expandable: true, items: [
      { id: 'all', label: 'All', count: '+1K' },
      { id: 'assigned', label: 'Assigned to Me' },
      { id: 'starred', label: 'Starred' },
      { id: 'notes', label: 'Notes', count: '48' },
      { id: 'archived', label: 'Archived' },
    ]},
    { id: 'team', label: 'Team', icon: null, expandable: true, items: [
      { id: 'team-all', label: 'All' },
      { id: 'team-starred', label: 'Starred' },
      { id: 'team-archived', label: 'Archived' },
    ]},
    { id: 'contacts', label: 'Contacts', icon: null, expandable: true, items: [
      { id: 'contacts-all', label: 'All' },
      { id: 'patients', label: 'Patients' },
      { id: 'invited', label: 'Patients Invited' },
      { id: 'with-app', label: 'Patients with app' },
      { id: 'unsaved', label: 'Unsaved Contacts' },
    ]},
    { id: 'scheduled', label: 'Scheduled', icon: <ScheduleIcon /> },
    { id: 'bulk', label: 'Bulk Messages', icon: null },
  ];

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 150px)', gap: 0, bgcolor: '#1a1a2e', flexDirection: { xs: 'column', md: 'row' } }}>
      {/* Left Sidebar - Spruce Style */}
      <Paper sx={{ 
        width: { xs: '100%', md: 180 }, 
        display: { xs: 'none', md: 'flex' }, 
        flexDirection: 'column', 
        borderRadius: 0,
        bgcolor: '#16213e',
        color: 'white',
        borderRight: '1px solid #0f3460'
      }}>
        {sidebarItems.map(section => (
          <Box key={section.id}>
            <Box
              onClick={() => section.expandable && toggleSection(section.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                cursor: section.expandable ? 'pointer' : 'default',
                '&:hover': section.expandable ? { bgcolor: 'rgba(255,255,255,0.05)' } : {},
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {section.icon}
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {section.label}
                </Typography>
              </Box>
              {section.expandable && (
                expandedSections.includes(section.id) 
                  ? <ExpandLessIcon fontSize="small" /> 
                  : <ExpandMoreIcon fontSize="small" />
              )}
            </Box>
            {section.items && (
              <Collapse in={expandedSections.includes(section.id)}>
                {section.items.map(item => (
                  <Box
                    key={item.id}
                    onClick={() => setSidebarSection(item.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      pl: 4,
                      pr: 2,
                      py: 0.5,
                      cursor: 'pointer',
                      bgcolor: sidebarSection === item.id ? 'rgba(255,255,255,0.1)' : 'transparent',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                    }}
                  >
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      {item.label}
                    </Typography>
                    {item.count && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        {item.count}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Collapse>
            )}
          </Box>
        ))}
      </Paper>

      {/* Conversation List */}
      <Paper sx={{ 
        width: { xs: selectedConversation ? '0px' : '100%', md: 320 }, 
        display: { xs: selectedConversation ? 'none' : 'flex', md: 'flex' }, 
        flexDirection: 'column', 
        borderRadius: 0,
        bgcolor: '#1f1f1f',
        color: 'white',
      }}>
        {/* Header */}
        <Box sx={{ p: 1.5, borderBottom: '1px solid #333', display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder="FILTER"
            select
            SelectProps={{ native: true }}
            sx={{ 
              width: 80,
              '& .MuiOutlinedInput-root': { 
                bgcolor: '#2d2d2d', 
                color: 'white',
                '& fieldset': { borderColor: '#444' }
              },
              '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' }
            }}
          >
            <option value="all">All</option>
            <option value="sms">SMS</option>
            <option value="fax">Fax</option>
          </TextField>
          <TextField
            size="small"
            placeholder="SORT"
            select
            SelectProps={{ native: true }}
            sx={{ 
              width: 120,
              '& .MuiOutlinedInput-root': { 
                bgcolor: '#2d2d2d', 
                color: 'white',
                '& fieldset': { borderColor: '#444' }
              },
              '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' }
            }}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </TextField>
        </Box>

        {/* Conversation List */}
        <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
          {filteredConversations.map(conv => (
            <ListItem
              key={conv.id}
              onClick={() => setSelectedConversation(conv)}
              sx={{
                borderBottom: '1px solid #333',
                py: 1.5,
                cursor: 'pointer',
                bgcolor: selectedConversation?.id === conv.id ? 'rgba(255,255,255,0.05)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1.5 }}>
                {/* Unread indicator */}
                {conv.unreadCount > 0 && (
                  <CircleIcon sx={{ fontSize: 8, color: '#4dabf5', mt: 1 }} />
                )}
                
                {/* Type icon */}
                <Avatar sx={{ 
                  width: 36, 
                  height: 36, 
                  bgcolor: conv.type === 'fax' ? '#555' : '#333',
                }}>
                  {getTypeIcon(conv.type, conv.unreadCount > 0)}
                </Avatar>
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ 
                      fontWeight: conv.unreadCount > 0 ? 600 : 400, 
                      color: 'white',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {conv.contactName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      {formatMessageDate(conv.lastMessageAt)}
                    </Typography>
                  </Box>
                  
                  {conv.assignedTo && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ArrowIcon sx={{ fontSize: 12, color: '#888' }} />
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {conv.assignedTo}
                      </Typography>
                    </Box>
                  )}
                  
                  <Typography variant="caption" sx={{ 
                    color: '#888',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}>
                    {conv.lastMessage}
                  </Typography>
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Message View */}
      {selectedConversation ? (
        <Box sx={{ flex: 1, display: { xs: selectedConversation ? 'flex' : 'none', md: 'flex' }, flexDirection: 'column', bgcolor: '#262626' }}>
          {/* Conversation Header */}
          <Box sx={{ 
            p: 1.5, 
            borderBottom: '1px solid #333', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            bgcolor: '#1f1f1f',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton 
                size="small" 
                sx={{ color: '#888', display: { xs: 'inline-flex', md: 'none' } }}
                onClick={() => setSelectedConversation(null)}
              >
                ←
              </IconButton>
              <Avatar sx={{ bgcolor: '#333' }}>
                {getTypeIcon(selectedConversation.type, false)}
              </Avatar>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 500 }}>
                {selectedConversation.contactName}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton size="small" sx={{ color: '#888' }}><PhoneIcon /></IconButton>
              <IconButton size="small" sx={{ color: '#888' }}><LinkIcon /></IconButton>
              <IconButton 
                size="small" 
                sx={{ color: selectedConversation.isStarred ? '#ffc107' : '#888' }}
                onClick={() => handleToggleStar(selectedConversation.id)}
              >
                {selectedConversation.isStarred ? <StarIcon /> : <StarBorderIcon />}
              </IconButton>
              <IconButton size="small" sx={{ color: '#888' }}><DescriptionIcon /></IconButton>
              <IconButton size="small" sx={{ color: '#888' }}><SettingsIcon /></IconButton>
              <IconButton 
                size="small" 
                sx={{ color: showDetails ? '#4dabf5' : '#888' }}
                onClick={() => setShowDetails(!showDetails)}
              >
                <InfoIcon />
              </IconButton>
              {showDetails && (
                <Button 
                  size="small" 
                  onClick={() => setShowDetails(false)}
                  sx={{ color: '#888', ml: 1 }}
                >
                  Close →
                </Button>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            {/* Messages */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {Object.entries(groupMessagesByDate(selectedConversation.messages)).map(([date, messages]) => (
                <Box key={date}>
                  {/* Date separator */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <Chip 
                      label={date} 
                      size="small" 
                      sx={{ 
                        bgcolor: '#333', 
                        color: 'white',
                        fontSize: '0.7rem',
                      }} 
                    />
                  </Box>
                  
                  {messages.map(msg => (
                    <Box
                      key={msg.id}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: msg.direction === 'outbound' ? 'flex-end' : 'flex-start',
                        mb: 1.5,
                      }}
                    >
                      {/* Author name for outbound */}
                      {msg.direction === 'outbound' && (
                        <Typography variant="caption" sx={{ color: '#888', mr: 1, mb: 0.5 }}>
                          {msg.author.displayName}
                        </Typography>
                      )}
                      
                      {/* Author name for inbound */}
                      {msg.direction === 'inbound' && (
                        <Typography variant="caption" sx={{ color: '#888', ml: 1, mb: 0.5 }}>
                          {msg.author.displayName}
                        </Typography>
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                        {msg.direction === 'inbound' && (
                          <Avatar sx={{ width: 28, height: 28, bgcolor: '#444', fontSize: '0.7rem' }}>
                            {msg.author.displayName.charAt(0)}
                          </Avatar>
                        )}
                        
                        <Box
                          sx={{
                            maxWidth: '70%',
                            bgcolor: msg.isRemoved 
                              ? '#4a4a4a'
                              : msg.isInternalNote
                              ? '#3d3d00'
                              : msg.direction === 'outbound'
                              ? '#2e7d32'
                              : '#333',
                            color: 'white',
                            p: 1.5,
                            borderRadius: 2,
                          }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {msg.text}
                          </Typography>
                          
                          {/* Attachments */}
                          {msg.attachments?.map(att => (
                            <Box key={att.id} sx={{ mt: 1, p: 1, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                              {att.type === 'fax' && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PdfIcon sx={{ color: '#ef5350' }} />
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2">{att.name}</Typography>
                                    <Typography 
                                      variant="caption" 
                                      sx={{ color: '#4dabf5', cursor: 'pointer' }}
                                      onClick={() => att.url && window.open(att.url, '_blank')}
                                    >
                                      View File
                                    </Typography>
                                  </Box>
                                  <IconButton 
                                    size="small" 
                                    sx={{ color: 'white' }}
                                    onClick={() => att.url && window.open(att.url, '_blank')}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                          ))}
                          
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mt: 0.5, textAlign: 'right' }}>
                            {format(parseISO(msg.createdAt), 'MMM d \'at\' h:mm a')}
                          </Typography>
                          
                          {msg.isRemoved && (
                            <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                              {msg.removedBy} removed a message.
                            </Typography>
                          )}
                        </Box>
                        
                        {msg.direction === 'outbound' && (
                          <Avatar sx={{ width: 28, height: 28, bgcolor: '#2e7d32', fontSize: '0.7rem' }}>
                            {msg.author.initials || msg.author.displayName.charAt(0)}
                          </Avatar>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))}
              <div ref={messagesEndRef} />
            </Box>

            {/* Details Panel */}
            {showDetails && (
              <Paper sx={{ 
                width: 280, 
                bgcolor: '#1f1f1f', 
                color: 'white',
                borderLeft: '1px solid #333',
                overflow: 'auto',
                p: 2,
              }}>
                <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>
                  Conversation Between
                </Typography>
                {selectedConversation.participants?.map((p, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: p.isTeam ? '#2e7d32' : '#444' }}>
                      {p.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{p.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>{p.phone}</Typography>
                    </Box>
                    {!p.isTeam && <IconButton size="small" sx={{ color: '#888', ml: 'auto' }}><PhoneIcon fontSize="small" /></IconButton>}
                  </Box>
                ))}
                
                {selectedConversation.tags && selectedConversation.tags.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {selectedConversation.tags.map(tag => (
                      <Chip 
                        key={tag} 
                        label={tag} 
                        size="small" 
                        sx={{ bgcolor: '#333', color: 'white', fontSize: '0.65rem' }} 
                      />
                    ))}
                  </Box>
                )}
                
                <Divider sx={{ my: 2, borderColor: '#333' }} />
                
                <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Subject</Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>add subject...</Typography>
                
                <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Assignment</Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>Search to assign...</Typography>
                
                <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Conversation Tags</Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>Search to select tags...</Typography>
                
                <Divider sx={{ my: 2, borderColor: '#333' }} />
                
                <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>More Options</Typography>
                <Button startIcon={<StarBorderIcon />} sx={{ color: 'white', justifyContent: 'flex-start', width: '100%' }}>
                  Star Conversation
                </Button>
                <Button startIcon={<ArchiveIcon />} sx={{ color: 'white', justifyContent: 'flex-start', width: '100%' }}>
                  Archive Conversation
                </Button>
                <Button startIcon={<CopyIcon />} sx={{ color: 'white', justifyContent: 'flex-start', width: '100%' }}>
                  Copy Conversation Link
                </Button>
                <Button startIcon={<DeleteIcon />} sx={{ color: '#ef5350', justifyContent: 'flex-start', width: '100%' }}>
                  Delete Conversation
                </Button>
              </Paper>
            )}
          </Box>

          {/* Message Input */}
          <Box sx={{ p: 2, borderTop: '1px solid #333', bgcolor: '#1f1f1f' }}>
            <ToggleButtonGroup
              value={messageType}
              exclusive
              onChange={(_, v) => v && setMessageType(v)}
              size="small"
              sx={{ mb: 1 }}
            >
              <ToggleButton value="sms" sx={{ color: 'white', borderColor: '#444', '&.Mui-selected': { bgcolor: '#2e7d32', color: 'white' } }}>
                SMS
              </ToggleButton>
              <ToggleButton value="note" sx={{ color: 'white', borderColor: '#444', '&.Mui-selected': { bgcolor: '#b8860b', color: 'white' } }}>
                Internal Note
              </ToggleButton>
            </ToggleButtonGroup>
            
            <TextField
              fullWidth
              placeholder={messageType === 'note' ? 'Add a note...' : 'Type a message...'}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
              multiline
              maxRows={4}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: messageType === 'note' ? 'rgba(184, 134, 11, 0.1)' : '#2d2d2d',
                  color: 'white',
                  '& fieldset': { borderColor: messageType === 'note' ? '#b8860b' : '#444' },
                },
              }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton size="small" sx={{ color: '#888' }}><AddIcon /></IconButton>
                <IconButton size="small" sx={{ color: '#888' }}><MicIcon /></IconButton>
                <IconButton size="small" sx={{ color: '#888' }}><FlagIcon /></IconButton>
                <IconButton size="small" sx={{ color: '#888' }}><ScheduleIcon /></IconButton>
                <IconButton size="small" sx={{ color: '#888' }}>@</IconButton>
                <IconButton size="small" sx={{ color: '#888' }}><EmojiIcon /></IconButton>
                <IconButton size="small" sx={{ color: '#888' }}><LinkIcon /></IconButton>
                <IconButton size="small" sx={{ color: '#888' }}><DescriptionIcon /></IconButton>
              </Box>
              <Button
                variant="contained"
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                endIcon={messageType === 'note' ? null : <SendIcon />}
                sx={{ 
                  bgcolor: messageType === 'note' ? '#b8860b' : '#2e7d32',
                  '&:hover': { bgcolor: messageType === 'note' ? '#8b6914' : '#1b5e20' },
                }}
              >
                {messageType === 'note' ? 'Post' : 'Send'}
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#262626' }}>
          <Box sx={{ textAlign: 'center' }}>
            <SmsIcon sx={{ fontSize: 64, color: '#444', mb: 2 }} />
            <Typography sx={{ color: '#888' }}>
              Select a conversation to view messages
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SpruceInbox;
